import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { MohammadAvatar } from "../avatar/MohammadAvatar";

import { heroGreetings as greetings, heroTechStack as techStack, heroContent } from "../../data/content";

export const HeroSection: React.FC = () => {
  const [greetingText, setGreetingText] = useState("Hello");
  const [isDeleting, setIsDeleting] = useState(false);
  const [greetingIndex, setGreetingIndex] = useState(0);

  // Cycling typing effect for greeting
  useEffect(() => {
    const currentWord = greetings[greetingIndex];
    const targetArr = Array.from(currentWord);
    const currentArr = Array.from(greetingText);
    let timer: ReturnType<typeof setTimeout>;

    if (isDeleting) {
      if (currentArr.length > 0) {
        timer = setTimeout(() => {
          setGreetingText(targetArr.slice(0, currentArr.length - 1).join(""));
        }, 45);
      } else {
        setIsDeleting(false);
        setGreetingIndex((prev) => (prev + 1) % greetings.length);
      }
    } else {
      if (currentArr.length < targetArr.length) {
        timer = setTimeout(() => {
          setGreetingText(targetArr.slice(0, currentArr.length + 1).join(""));
        }, 90);
      } else {
        timer = setTimeout(() => {
          setIsDeleting(true);
        }, 2200);
      }
    }

    return () => clearTimeout(timer);
  }, [greetingText, isDeleting, greetingIndex]);

  return (
    <section
      id="hero"
      className="pt-28 sm:pt-36 pb-16 sm:pb-20 flex flex-col justify-center relative border-b border-[#242320] scroll-mt-28"
    >
      {/* Warm Ambient Radial Glow Behind Hero */}
      <div
        aria-hidden="true"
        className="absolute top-12 left-1/2 -translate-x-1/2 w-full max-w-4xl h-96 bg-gradient-to-b from-[#E5983A]/8 via-[#D97706]/3 to-transparent blur-3xl pointer-events-none -z-10"
      />

      <div className="max-w-5xl mx-auto w-full px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
          {/* Left Text Column */}
          <div className="lg:col-span-8 space-y-6">
            {/* Typing Greeting with Blinking Amber Bar */}
            <div className="h-10 sm:h-12 flex items-center">
              <h1
                id="hero-greeting-heading"
                className="text-[28px] font-normal tracking-tight leading-none text-[#D4D4D4] flex items-center min-h-[32px]"
              >
                <span>{greetingText}</span>
                <motion.span
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                  className="inline-block w-[2.5px] h-[24px] ml-1.5 bg-[#E5983A] rounded-full align-middle"
                  aria-hidden="true"
                />
              </h1>
            </div>

            {/* Editorial Statement */}
            <p
              style={{ fontSize: "clamp(28px, 4vw, 36px)", lineHeight: "1.25" }}
              className="font-display font-medium text-[#D4D4D4] max-w-3xl"
            >
              {heroContent.statementLead}{" "}
              {heroContent.statementHighlights.map((hl, i) => (
                <React.Fragment key={i}>
                  <span className="underline underline-offset-4 decoration-[#8C8A84] text-[#F3F2EE]">
                    {hl.text}
                  </span>
                  {i < heroContent.statementHighlights.length - 1
                    ? i === heroContent.statementHighlights.length - 2
                      ? ", and "
                      : ", "
                    : "."}
                </React.Fragment>
              ))}
            </p>

            {/* Short Bio Statement */}
            <p className="text-base text-[#A6A39A] leading-relaxed max-w-2xl">
              {heroContent.bio}
            </p>
          </div>

          {/* Right Column: Interactive Dual-SVG Avatar */}
          <div className="lg:col-span-4 flex flex-col items-center justify-center pt-2 lg:pt-0">
            <MohammadAvatar className="w-56 h-64 sm:w-64 sm:h-72 lg:w-72 lg:h-80" />
          </div>
        </div>

        {/* Core Stack Ribbon */}
        <div id="hero-worked-with" className="pt-6 sm:pt-8 border-t border-[#242320]">
          <div className="flex items-center justify-between mb-3.5">
            <h3 className="text-xs font-mono uppercase tracking-widest text-[#8C8A84]">
              Core Technologies & Environments
            </h3>
          </div>
          <div className="flex flex-wrap gap-2.5 sm:gap-3 items-center">
            {techStack.map((tech) => (
              <div
                key={tech.name}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#181716] border border-[#2A2926] text-xs font-mono text-[#C8C6BE] hover:border-[#E5983A]/40 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#E5983A]" />
                <span className="font-medium text-[#F3F2EE]">{tech.name}</span>
                <span className="text-[10px] text-[#7A7870]">· {tech.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
